import { type BaseRecord, type HttpError, useUpdate } from "@refinedev/core";
import {
  type QueryClient,
  useQueryClient,
} from "@tanstack/react-query";
import { useCallback } from "react";

/** Must match InfiniteList’s useCustom queryKey: ["custom", endpoint, …]. */
async function invalidateInfiniteListEndpoint(
  queryClient: QueryClient,
  endpoint: string | string[]
): Promise<void> {
  const endpoints = new Set(Array.isArray(endpoint) ? endpoint : [endpoint]);
  await queryClient.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey;
      return (
        Array.isArray(key) &&
        key[0] === "custom" &&
        typeof key[1] === "string" &&
        endpoints.has(key[1] as string)
      );
    },
    refetchType: "all",
  });
}

/** User lists use endpoints like `users/:pending` — all start with `users/`. */
async function invalidateUserInfiniteLists(
  queryClient: QueryClient
): Promise<void> {
  await queryClient.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey;
      return (
        Array.isArray(key) &&
        key[0] === "custom" &&
        typeof key[1] === "string" &&
        (key[1] as string).startsWith("users/")
      );
    },
    refetchType: "all",
  });
}

/**
 * Maps `useUpdate` `resource` strings to InfiniteList GET path(s) (2nd segment of
 * ["custom", endpoint, …] query keys). Extend this when a new PATCH resource
 * should refresh infinite-scrolled admin lists.
 */
export const UPDATE_RESOURCE_TO_INFINITE_LIST_ENDPOINT: Record<
  string,
  string | string[] | "users"
> = {
  "grant-vestment-requests": "grant-vestment-requests",
  "contribution-transactions": "contribution-transactions",
  "investments-request": "investments-requests",
  "grant-requests": "grant-requests",
  "transfer-transactions": "transfer-transactions",
  "recommended-charity": "recommended-charity",
  "users/handelUserRequest": "users",
};

type HookUpdateParams<
  TData extends BaseRecord = BaseRecord,
  TError extends HttpError = HttpError,
  TVariables = unknown,
> = Parameters<
  ReturnType<typeof useUpdate<TData, TError, TVariables>>["mutate"]
>[0];

export type UpdateWithInfiniteListParams<
  TData extends BaseRecord = BaseRecord,
  TError extends HttpError = HttpError,
  TVariables = unknown,
> = HookUpdateParams<TData, TError, TVariables> & {
  /**
   * - `undefined`: use {@link UPDATE_RESOURCE_TO_INFINITE_LIST_ENDPOINT} for `resource`
   * - `false`: skip infinite-list invalidation (Refine list/detail invalidation still runs)
   * - `"users"`: invalidate all `users/…` infinite lists
   * - string | string[]: invalidate those endpoint(s) exactly
   */
  infiniteList?: false | "users" | string | string[];
};

async function runInfiniteListInvalidation(
  queryClient: ReturnType<typeof useQueryClient>,
  resource: string,
  infiniteList: UpdateWithInfiniteListParams["infiniteList"]
): Promise<void> {
  const target =
    infiniteList !== undefined
      ? infiniteList
      : UPDATE_RESOURCE_TO_INFINITE_LIST_ENDPOINT[resource];

  if (target === undefined || target === false) return;

  if (target === "users") {
    await invalidateUserInfiniteLists(queryClient);
  } else {
    await invalidateInfiniteListEndpoint(queryClient, target);
  }
}

/**
 * Drop-in replacement for `useUpdate` that invalidates InfiniteList React Query
 * caches after a successful update, using {@link UPDATE_RESOURCE_TO_INFINITE_LIST_ENDPOINT}.
 */
export function useUpdateWithInfiniteListInvalidation<
  TData extends BaseRecord = BaseRecord,
  TError extends HttpError = HttpError,
  TVariables = Record<string, unknown>,
>(props?: Parameters<typeof useUpdate<TData, TError, TVariables>>[0]) {
  const queryClient = useQueryClient();
  const result = useUpdate<TData, TError, TVariables>(props);

  const mutate = useCallback(
    (
      params: UpdateWithInfiniteListParams<TData, TError, TVariables>,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      mutateOptions?: any
    ) => {
      const { infiniteList, ...baseParams } = params;
      const userOnSuccess = mutateOptions?.onSuccess;

      result.mutate(baseParams, {
        ...mutateOptions,
        onSuccess: async (
          data: unknown,
          variables: { resource: string },
          context: unknown
        ) => {
          await runInfiniteListInvalidation(
            queryClient,
            variables.resource,
            infiniteList
          );
          await userOnSuccess?.(data, variables, context);
        },
      });
    },
    [queryClient, result]
  );

  const mutateAsync = useCallback(
    async (
      params: UpdateWithInfiniteListParams<TData, TError, TVariables>,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      mutateOptions?: any
    ) => {
      const { infiniteList, ...baseParams } = params;
      const userOnSuccess = mutateOptions?.onSuccess;

      return result.mutateAsync(baseParams, {
        ...mutateOptions,
        onSuccess: async (
          data: unknown,
          variables: { resource: string },
          context: unknown
        ) => {
          await runInfiniteListInvalidation(
            queryClient,
            variables.resource,
            infiniteList
          );
          await userOnSuccess?.(data, variables, context);
        },
      });
    },
    [queryClient, result]
  );

  return {
    ...result,
    mutate,
    mutateAsync,
  };
}
